import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface FactorDetail {
  source: string;
  factor: string;
  hit: boolean;
  weight: number;
  category: string;
  result?: string;
  raw_inputs?: Record<string, unknown>;
  raw_output?: boolean;
  disabled?: boolean;
  disabled_reason?: string;
}

interface ScoredProduct {
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  original_coo_country?: string;
  new_coo_country?: string | null;
  coo_score: number;
  quality_tier: string;
  original_score: number | null;
  original_coo_rescore?: number | null;
  original_coo_factor_details?: FactorDetail[] | null;
  original_coo_hit_count?: number | null;
  factor_details: FactorDetail[];
  factor_results: Record<string, boolean>;
  scored_at: string;
  run_id: string;
  enabled_hit_count?: number;
  total_enabled_factors?: number;
  total_disabled_factors?: number;
}

// ============================================================
// AGENT DISPLAY NAMES
// ============================================================

const AGENT_DISPLAY: Record<string, string> = {
  coo_scoring: 'COO Scoring Agent',
  ndc_lookup: 'NDC Lookup Agent',
  qrs: 'Quality Rating System',
  pis: 'Pricing Intelligence System',
  coors: 'COORS Agent',
  pns: 'Political Nexus Scoring',
};

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function tierColor(tier: string): string {
  switch (tier) {
    case 'HIGH': return '#22C55E';
    case 'MODERATE': return '#F59E0B';
    case 'LOW': return '#EF4444';
    default: return '#94A3B8';
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function ScoredProducts() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [products, setProducts] = useState<ScoredProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedFactors, setExpandedFactors] = useState<Set<string>>(new Set());
  const [collapsedSources, setCollapsedSources] = useState<Set<string>>(new Set());
  const [removingNdc, setRemovingNdc] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Track which COO view is active per row: 'new' (default) or 'original'
  const [cooView, setCooView] = useState<Record<string, 'new' | 'original'>>({});

  const agentDisplay = AGENT_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');
  const isCoo = agentName === 'coo_scoring';

  const fetchProducts = useCallback(async () => {
    if (!agentName) return;
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/agents/${agentName}/scored-products`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        setProducts(data.products || []);
        setError(null);
      } else {
        setError('Failed to fetch scored products');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch scored products');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleRemoveRescore = async (ndc: string, e: React.MouseEvent) => {
    e.stopPropagation(); // prevent row expand
    if (!agentName) return;
    if (!window.confirm(`Remove rescore for NDC ${ndc}? The original score will be restored.`)) return;
    setRemovingNdc(ndc);
    setSuccessMsg(null);
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/agents/${agentName}/scored-products/${ndc}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (resp.ok) {
        setProducts(prev => prev.filter(p => p.ndc !== ndc));
        setSuccessMsg(`Rescore removed for NDC ${ndc}. Original score restored.`);
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        const data = await resp.json().catch(() => null);
        setError(data?.detail || 'Failed to remove rescore');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove rescore');
    } finally {
      setRemovingNdc(null);
    }
  };

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleFactor = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedFactors(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSource = (key: string) => {
    setCollapsedSources(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const SOURCE_DISPLAY: Record<string, string> = {
    // COO sources
    openfda_ndc: 'OpenFDA NDC Directory',
    fda_import_refusals: 'FDA Import Refusals',
    epa_tri: 'EPA Toxics Release Inventory',
    fda_shortages: 'FDA Drug Shortages',
    fda_recalls: 'FDA Recalls',
    dailymed_spl: 'DailyMed SPL',
    package_ocr: 'Package OCR (AI Vision)',
    sec_edgar: 'SEC EDGAR 10-K Filings',
    // QRS sources
    fda_faers: 'FDA FAERS Adverse Events',
    fda_inspections: 'FDA Inspections / 483s',
  };

  const isQrs = agentName === 'qrs';

  const SOURCE_ORDER = isQrs
    ? ['fda_recalls', 'fda_faers', 'fda_shortages', 'fda_inspections']
    : [
        'openfda_ndc', 'fda_import_refusals',
        'epa_tri', 'fda_shortages', 'fda_recalls',
        'dailymed_spl', 'package_ocr', 'sec_edgar',
      ];

  const groupFactorsBySource = (factors: FactorDetail[]): Record<string, FactorDetail[]> => {
    const grouped: Record<string, FactorDetail[]> = {};
    for (const f of factors) {
      if (!grouped[f.source]) grouped[f.source] = [];
      grouped[f.source].push(f);
    }
    return grouped;
  };

  const renderRawData = (data: Record<string, unknown>): React.ReactNode => {
    if (!data || Object.keys(data).length === 0) return <span style={{ color: T.textLight, fontSize: 11 }}>No raw data</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(data).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', gap: 8, fontSize: 11, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: T.textDark, minWidth: 140, flexShrink: 0 }}>
              {key.replace(/_/g, ' ')}:
            </span>
            <span style={{ color: T.textLight, wordBreak: 'break-word' }}>
              {Array.isArray(value)
                ? value.length === 0
                  ? '(empty)'
                  : typeof value[0] === 'object'
                    ? JSON.stringify(value, null, 2)
                    : value.join(', ')
                : typeof value === 'boolean'
                  ? value ? 'true' : 'false'
                  : typeof value === 'object' && value !== null
                    ? JSON.stringify(value, null, 2)
                    : String(value || '(empty)')}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading scored products...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to {agentDisplay}
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Products Scored
            </h1>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              All products successfully rescored by the {agentDisplay}. Click a row to see factor breakdown.
            </p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: `${T.navy}08`, borderRadius: 12, padding: '16px 24px',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
              Total Scored
            </span>
            <span style={{ fontSize: 32, fontWeight: 800, color: T.textDark }}>
              {products.length}
            </span>
          </div>
        </div>
      </div>

      {successMsg && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: '#ECFDF5', border: '1px solid #22C55E30',
          color: '#16A34A', fontSize: 13, fontWeight: 600,
        }}>
          {successMsg}
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* Products Table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {products.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
              No Products Scored Yet
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              Use the Re-score feature on the agent detail page to score products.
              Successfully scored products will appear here.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {(isCoo
                    ? ['NDC #', 'Product Name', 'Manufacturer', 'Original COO', 'Original Score', 'New COO', 'Latest Score', 'Scored At', '']
                    : ['NDC #', 'Product Name', 'Manufacturer', 'Score', 'Quality Tier', 'Scored At', '']
                  ).map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => {
                  const rowKey = `${product.ndc}-${product.run_id}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  const enabledDetails = product.factor_details?.filter(f => !f.disabled) ?? [];
                  const disabledDetails = product.factor_details?.filter(f => f.disabled) ?? [];
                  const hitCount = enabledDetails.filter(f => f.hit).length;
                  const totalFactors = enabledDetails.length;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        style={{
                          borderBottom: `1px solid ${T.borderLight}`,
                          cursor: 'pointer',
                          background: isExpanded ? `${T.gold}08` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke={T.textLight} strokeWidth="2"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                            {product.ndc}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {product.product_name || '--'}
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {product.manufacturer || '--'}
                        </td>
                        {isCoo && (
                          <td style={{ padding: '12px 16px', color: T.textDark }}>
                            {product.original_coo_country || product.coo_country || '--'}
                          </td>
                        )}
                        {isCoo && (
                          <td style={{ padding: '12px 16px', color: T.textLight }}>
                            {product.original_score != null ? `${product.original_score}%` : '--'}
                          </td>
                        )}
                        {isCoo && (
                          <td style={{ padding: '12px 16px', color: product.new_coo_country ? '#DC2626' : T.textLight, fontWeight: product.new_coo_country ? 600 : 400 }}>
                            {product.new_coo_country || '--'}
                          </td>
                        )}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                              fontSize: 14, fontWeight: 700,
                              color: tierColor(product.quality_tier),
                            }}>
                              {product.coo_score}%
                            </span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                              background: `${tierColor(product.quality_tier)}18`,
                              color: tierColor(product.quality_tier),
                            }}>
                              {product.quality_tier || '--'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(product.scored_at)}
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={(e) => handleRemoveRescore(product.ndc, e)}
                            disabled={removingNdc === product.ndc}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              border: `1px solid ${T.danger}40`, background: `${T.danger}10`,
                              color: T.danger, cursor: removingNdc === product.ndc ? 'not-allowed' : 'pointer',
                              opacity: removingNdc === product.ndc ? 0.6 : 1,
                              fontFamily: 'inherit', transition: 'all 0.15s',
                            }}
                          >
                            {removingNdc === product.ndc ? 'Removing...' : 'Remove Rescore'}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded factor breakdown */}
                      {isExpanded && (() => {
                        const hasCooOverride = !!(product.new_coo_country && product.original_coo_factor_details);
                        const activeView = cooView[rowKey] || 'new';
                        const displayDetails = (hasCooOverride && activeView === 'original')
                          ? product.original_coo_factor_details!
                          : product.factor_details;
                        const displayEnabledDetails = displayDetails?.filter(f => !f.disabled) ?? [];
                        const displayDisabledDetails = displayDetails?.filter(f => f.disabled) ?? [];
                        const displayHitCount = displayEnabledDetails.filter(f => f.hit).length;
                        const displayTotalFactors = displayEnabledDetails.length;
                        const displayScore = (hasCooOverride && activeView === 'original')
                          ? (product.original_coo_rescore != null ? Math.round(product.original_coo_rescore) : null)
                          : product.coo_score;
                        const displayCoo = (hasCooOverride && activeView === 'original')
                          ? (product.original_coo_country || product.coo_country)
                          : product.coo_country;
                        return (
                        <tr>
                          <td colSpan={isCoo ? 9 : 7} style={{ padding: 0 }}>
                            <div style={{
                              padding: '16px 24px 20px',
                              background: `linear-gradient(180deg, ${T.offWhite} 0%, ${T.white} 100%)`,
                              borderBottom: `2px solid ${T.gold}30`,
                            }}>
                              {/* COO Toggle Buttons — only shown when override occurred */}
                              {hasCooOverride && (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  marginBottom: 14, padding: '10px 14px',
                                  background: `${T.navy}06`, borderRadius: 8,
                                  border: `1px solid ${T.border}`,
                                }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, marginRight: 4 }}>
                                    VIEW:
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setCooView(prev => ({ ...prev, [rowKey]: 'original' })); }}
                                    style={{
                                      padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                      border: activeView === 'original' ? `2px solid ${T.navy}` : `1px solid ${T.border}`,
                                      background: activeView === 'original' ? `${T.navy}10` : T.white,
                                      color: activeView === 'original' ? T.navy : T.textLight,
                                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                                    }}
                                  >
                                    Rescore with Original COO ({product.original_coo_country || '--'})
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setCooView(prev => ({ ...prev, [rowKey]: 'new' })); }}
                                    style={{
                                      padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                      border: activeView === 'new' ? `2px solid ${T.gold}` : `1px solid ${T.border}`,
                                      background: activeView === 'new' ? `${T.gold}10` : T.white,
                                      color: activeView === 'new' ? T.gold : T.textLight,
                                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                                    }}
                                  >
                                    Rescore with New COO ({product.new_coo_country || '--'})
                                  </button>
                                </div>
                              )}

                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginBottom: 12,
                              }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 700, color: T.textLight,
                                  textTransform: 'uppercase', letterSpacing: '0.1em',
                                }}>
                                  Factor Breakdown{hasCooOverride ? ` — ${displayCoo}` : ''}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>
                                  {displayHitCount} of {displayTotalFactors} factors hit{displayDisabledDetails.length > 0 ? ` (${displayDisabledDetails.length} disabled)` : ''}
                                </span>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {(() => {
                                  const grouped = groupFactorsBySource(displayDetails || []);
                                  const sortedSources = SOURCE_ORDER.filter(s => grouped[s]);
                                  // Add any sources not in ORDER
                                  Object.keys(grouped).forEach(s => { if (!sortedSources.includes(s)) sortedSources.push(s); });
                                  return sortedSources.map(sourceName => {
                                    const factors = grouped[sourceName];
                                      const enabledFactors = factors.filter(f => !f.disabled);
                                      const disabledFactors = factors.filter(f => f.disabled);
                                      const sourceHits = enabledFactors.filter(f => f.hit).length;
                                      const sourceKey = `${rowKey}-src-${sourceName}`;
                                      const isSourceCollapsed = collapsedSources.has(sourceKey);
                                      const sourceWeight = enabledFactors.reduce((sum, f) => sum + (f.hit ? f.weight : 0), 0);
                                      const allDisabled = enabledFactors.length === 0;
                                    return (
                                      <div key={sourceName} style={{
                                        border: `1px solid ${T.border}`,
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                      }}>
                                        {/* Source header */}
                                        <div
                                          onClick={() => toggleSource(sourceKey)}
                                          style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '10px 14px', cursor: 'pointer',
                                            background: sourceHits > 0 ? `${T.success}06` : T.offWhite,
                                            borderBottom: isSourceCollapsed ? 'none' : `1px solid ${T.border}`,
                                            transition: 'background 0.15s',
                                          }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <svg
                                              width="12" height="12" viewBox="0 0 24 24" fill="none"
                                              stroke={T.textLight} strokeWidth="2.5"
                                              style={{
                                                transition: 'transform 0.2s',
                                                transform: isSourceCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                              }}
                                            >
                                              <path d="M6 9l6 6 6-6" />
                                            </svg>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: T.navy }}>
                                              {SOURCE_DISPLAY[sourceName] || sourceName.replace(/_/g, ' ')}
                                            </span>
                                            <span style={{
                                              fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 8,
                                              background: sourceHits > 0 ? `${T.success}15` : `${T.textLight}15`,
                                              color: sourceHits > 0 ? T.success : T.textLight,
                                            }}>
                                              {sourceHits}/{enabledFactors.length} hit{disabledFactors.length > 0 ? ` (${disabledFactors.length} disabled)` : ''}
                                            </span>
                                          </div>
                                          <span style={{ fontSize: 12, fontWeight: 700, color: sourceWeight > 0 ? T.success : T.textLight }}>
                                            +{sourceWeight.toFixed(2)}%
                                          </span>
                                        </div>

                                        {/* Factors within source */}
                                        {!isSourceCollapsed && (
                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {factors.map((factor, fIdx) => {
                                              const factorKey = `${rowKey}-${factor.factor}-${fIdx}`;
                                              const isFactorExpanded = expandedFactors.has(factorKey);
                                              return (
                                                <div key={factorKey} style={{
                                                  borderBottom: fIdx < factors.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                                                }}>
                                                  {/* Factor row */}
                                                  <div
                                                    onClick={(e) => toggleFactor(factorKey, e)}
                                                    style={{
                                                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                                                      padding: '10px 14px', cursor: 'pointer',
                                                      background: factor.disabled ? `${T.textLight}06` : factor.hit ? `${T.success}04` : T.white,
                                                      transition: 'background 0.15s',
                                                      opacity: factor.disabled ? 0.55 : 1,
                                                    }}
                                                  >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                                                      <div style={{
                                                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 4,
                                                        background: factor.disabled ? T.textLight : factor.hit ? T.success : T.textLight,
                                                        opacity: factor.disabled ? 0.3 : factor.hit ? 1 : 0.3,
                                                      }} />
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                          <span style={{ fontSize: 12, fontWeight: 600, color: factor.disabled ? T.textLight : T.textDark }}>
                                                            {factor.factor.replace(/_/g, ' ')}
                                                          </span>
                                                          <svg
                                                            width="10" height="10" viewBox="0 0 24 24" fill="none"
                                                            stroke={T.textLight} strokeWidth="2"
                                                            style={{
                                                              transition: 'transform 0.2s', flexShrink: 0,
                                                              transform: isFactorExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            }}
                                                          >
                                                            <path d="M6 9l6 6 6-6" />
                                                          </svg>
                                                          {factor.disabled && (
                                                            <span style={{
                                                              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                                              background: `${T.textLight}15`, color: T.textLight,
                                                              textTransform: 'uppercase', letterSpacing: '0.05em',
                                                            }}>
                                                              Disabled
                                                            </span>
                                                          )}
                                                        </div>
                                                        {factor.disabled && factor.disabled_reason && (
                                                          <div style={{
                                                            fontSize: 10, color: T.textLight, marginTop: 3, lineHeight: 1.4,
                                                            fontStyle: 'italic',
                                                          }}>
                                                            {factor.disabled_reason}
                                                          </div>
                                                        )}
                                                        {factor.result && !factor.disabled && (
                                                          <div style={{
                                                            fontSize: 11, color: factor.hit ? '#16A34A' : '#94A3B8',
                                                            marginTop: 4, lineHeight: 1.5,
                                                            fontStyle: 'italic',
                                                            ...(isFactorExpanded ? {} : {
                                                              overflow: 'hidden', textOverflow: 'ellipsis',
                                                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
                                                            }),
                                                          }}>
                                                            {factor.result}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div style={{
                                                      fontSize: 12, fontWeight: 700, flexShrink: 0, marginLeft: 12,
                                                      color: factor.disabled ? T.textLight : factor.hit ? T.success : T.textLight,
                                                    }}>
                                                      {factor.disabled ? '--' : factor.hit ? `+${factor.weight}%` : '0%'}
                                                    </div>
                                                  </div>

                                                  {/* Expanded raw inputs/outputs */}
                                                  {isFactorExpanded && (
                                                    <div style={{
                                                      padding: '0 14px 12px 32px',
                                                      background: `${T.navy}04`,
                                                    }}>
                                                      <div style={{
                                                        padding: '10px 14px', borderRadius: 6,
                                                        border: `1px solid ${T.border}`,
                                                        background: T.white,
                                                      }}>
                                                        <div style={{
                                                          fontSize: 10, fontWeight: 700, color: T.textLight,
                                                          textTransform: 'uppercase', letterSpacing: '0.08em',
                                                          marginBottom: 8,
                                                        }}>
                                                          Raw Inputs
                                                        </div>
                                                        {factor.raw_inputs ? renderRawData(factor.raw_inputs) : (
                                                          <span style={{ color: T.textLight, fontSize: 11 }}>No raw input data available</span>
                                                        )}
                                                        <div style={{
                                                          marginTop: 10, paddingTop: 8,
                                                          borderTop: `1px solid ${T.borderLight}`,
                                                          fontSize: 10, fontWeight: 700, color: T.textLight,
                                                          textTransform: 'uppercase', letterSpacing: '0.08em',
                                                          marginBottom: 4,
                                                        }}>
                                                          Output
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                                          <span style={{
                                                            display: 'inline-block', padding: '2px 10px', borderRadius: 4,
                                                            fontWeight: 700, fontSize: 11,
                                                            background: factor.hit ? `${T.success}15` : `${T.danger}10`,
                                                            color: factor.hit ? T.success : T.danger,
                                                          }}>
                                                            {factor.hit ? 'HIT (true)' : 'MISS (false)'}
                                                          </span>
                                                          <span style={{ color: T.textLight, fontSize: 11 }}>
                                                            Weight: {factor.hit ? `+${factor.weight}%` : '0%'}
                                                          </span>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>

                              {/* Score summary bar */}
                              <div style={{
                                marginTop: 12, padding: '10px 16px', borderRadius: 8,
                                background: T.navy, display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between',
                              }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
                                  Total Score{hasCooOverride ? ` (${displayCoo})` : ''}
                                </span>
                                <span style={{
                                  fontSize: 18, fontWeight: 800,
                                  color: tierColor(product.quality_tier),
                                }}>
                                  {displayScore != null ? `${displayScore}%` : '--'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ); })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
